import { useEffect, useState, type ReactNode } from 'react'
import { Bell, BellOff, ChevronRight, Smartphone, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { useToast } from '../ui/use-toast'
import { usePushNudge } from '../../hooks/usePushNudge'
import { pushTeaserService, type PushTeaser } from '../../services/pushTeaser'
import { teaserSentence } from '../../utils/push-teaser-sentence'
import type { PushSource } from '../../services/pushNotification'
import { dismissPushCard, isPushCardDismissed } from '../../utils/push-nudge-storage'
import { detectInstallPlatform } from '../../utils/install-prompt'
import { trackEvent } from '../../utils/analytics'
import { InstallStepsList } from '../pwa/InstallSteps'
import { PushUnblockSteps } from './PushUnblockSteps'

type Placement = 'dashboard' | 'climbs' | 'notifications'

const SOURCE: Record<Placement, PushSource> = {
  dashboard: 'dashboard_card',
  climbs: 'climbs_card',
  notifications: 'notifications_card',
}

// Once per visit for each place and question, not on every render.
const tracked = new Set<string>()

interface PushNudgeCardProps {
  placement: Placement
  /** Drawn instead when there is nothing to ask (the install card, on the dashboard). */
  fallback?: ReactNode
}

/**
 * "Turn notifications on", as a slim card at the top of the dashboard,
 * `/climbs` and `/notifications` (issue #351, docs/PUSH_OPT_IN_PLAN.md).
 *
 * It asks only somebody push does not reach on any device yet, and in the
 * words of what they would have had: this week's new climbs at their gym, the
 * comments on their sends. Tapping it opens the browser's own prompt; where
 * that was blocked it shows how to lift the block, and on an iPhone outside
 * the app it offers the install, the only way notifications arrive there.
 *
 * The cross hides it for longer each time, then for good
 * (`push-nudge-storage`), on all three pages at once.
 */
export function PushNudgeCard({ placement, fallback = null }: PushNudgeCardProps) {
  const { t } = useTranslation()
  const { toast } = useToast()
  const { nudge, userId, enable, busy } = usePushNudge()
  const [, setDismissals] = useState(0)
  const [teaser, setTeaser] = useState<PushTeaser | null>(null)
  const [showSteps, setShowSteps] = useState(false)
  const source = SOURCE[placement]

  const asking = nudge === 'ask' || nudge === 'blocked' || nudge === 'install'
  const visible = asking && userId !== null && !isPushCardDismissed(userId, nudge)

  useEffect(() => {
    if (nudge !== 'ask' || !userId || !visible) return
    let current = true
    pushTeaserService.get(userId).then(
      (loaded) => current && setTeaser(loaded),
      () => current && setTeaser(null),
    )
    return () => {
      current = false
    }
  }, [nudge, userId, visible])

  useEffect(() => {
    if (!visible) return
    const key = `${source}:${nudge}`
    if (tracked.has(key)) return
    tracked.add(key)
    trackEvent('push_nudge_shown', { source, state: nudge })
  }, [visible, source, nudge])

  if (nudge === 'loading') return null
  if (!visible || !userId) return <>{fallback}</>

  const installPlatform = nudge === 'install' ? detectInstallPlatform() : null

  const open = async () => {
    trackEvent('push_nudge_accepted', { source, state: nudge })
    if (nudge !== 'ask') {
      setShowSteps(true)
      return
    }
    try {
      await enable(source)
      trackEvent('push_enabled', { source })
      toast({ title: t('pushNudge.enabledToast.title'), description: t('pushNudge.enabledToast.description') })
    } catch {
      trackEvent('push_permission_denied', { source })
      toast({ title: t('pushNudge.deniedToast.title'), description: t('pushNudge.deniedToast.description') })
    }
  }

  const dismiss = () => {
    dismissPushCard(userId, nudge)
    trackEvent('push_nudge_dismissed', { source, state: nudge })
    setDismissals((count) => count + 1)
  }

  const Icon = nudge === 'blocked' ? BellOff : nudge === 'install' ? Smartphone : Bell
  const title = nudge === 'ask' && placement === 'notifications'
    ? t('pushNudge.card.ask.titleNotifications')
    : t(`pushNudge.card.${nudge}.title`)
  const body = nudge === 'ask' ? teaserSentence(t, teaser) : t(`pushNudge.card.${nudge}.description`)

  return (
    <>
      <div className="flex items-center gap-1 rounded-xl border bg-card pr-1 text-card-foreground shadow-sm">
        <button
          type="button"
          onClick={open}
          disabled={busy && nudge === 'ask'}
          className="flex min-w-0 flex-1 items-center gap-3 rounded-l-xl px-3 py-2.5 text-left transition-colors hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-70"
        >
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-semibold">{title}</span>
            <span className="block truncate text-xs text-muted-foreground">{body}</span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t('pushNudge.card.dismiss')}
          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {nudge === 'blocked' && <PushUnblockSteps open={showSteps} onOpenChange={setShowSteps} />}

      {nudge === 'install' && (
        <Dialog open={showSteps} onOpenChange={setShowSteps}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle>{t('pushNudge.install.title')}</DialogTitle>
              <DialogDescription>{t('pushNudge.install.description')}</DialogDescription>
            </DialogHeader>
            {installPlatform && <InstallStepsList platform={installPlatform} />}
          </DialogContent>
        </Dialog>
      )}
    </>
  )
}
